/**
 * Backend API Client
 * Handles communication with the translation backend server
 */
import { DialogAppSDK, PageAppSDK, SidebarAppSDK } from "@contentful/app-sdk";

// Backend API URL - empty string means same origin (unified server)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Polling interval in milliseconds
const POLL_INTERVAL = 2000;

export type JobType = 'Export' | 'AI Translation' | 'Import';
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface JobFailedItem {
  entryId: string;
  error: string;
}

export interface JobResult {
  translatedCount?: number;
  submittedCount?: number;
  importedCount?: number;
}

export interface Job {
  id: string;
  type: JobType;
  status: JobStatus;
  progress: number;
  title: string;
  spaceId: string;
  sourceLanguage: string;
  targetLanguages: string[];
  totalItems: number;
  processedItems: number;
  failedItems: JobFailedItem[];
  result?: JobResult;
  error?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface JobsListResponse {
  success: boolean;
  data?: {
    jobs: Job[];
    total: number;
    limit: number;
    offset: number;
  };
  error?: string;
}

export interface JobSubmitResponse {
  success: boolean;
  data?: {
    jobId: string;
  };
  error?: string;
}

export interface JobStatusResponse {
  success: boolean;
  data?: {
    job: Job;
  };
  error?: string;
}

export interface TranslationResponse {
  success: boolean;
  data?: {
    translatedCount?: number;
    submittedCount?: number;
    importedCount?: number;
    languages?: string[];
    workflow?: string;
  };
  error?: string;
}

export type ProgressCallback = (job: Job) => void;

/**
 * Gets GPI credentials from SDK installation parameters
 */
function getGPICredentials(sdk: DialogAppSDK | PageAppSDK | SidebarAppSDK) {
  const params: any = sdk.parameters.installation;
  return {
    gpiSecretKey: params.secretKey || '',
    gpiAuthToken: params.authToken || '',
    gpiCompanyId: params.companyId || ''
  };
}

/**
 * Gets the CMA access token from installation parameters
 */
function getAccessToken(sdk: DialogAppSDK | PageAppSDK | SidebarAppSDK): string {
  const params: any = sdk.parameters.installation;
  return params.cmaToken || '';
}

/**
 * Translates a single entry (for Sidebar)
 * Returns immediately with a jobId - use pollJobUntilComplete to track progress
 */
export async function translateSingleEntry(
  sdk: SidebarAppSDK,
  entryId: string,
  contentType: string,
  fields: Record<string, any>,
  sourceLanguage: string,
  targetLanguages: string[],
  translationService: string
): Promise<JobSubmitResponse> {
  const gpiCredentials = getGPICredentials(sdk);
  const accessToken = getAccessToken(sdk);

  const requestBody = {
    accessToken,
    spaceId: sdk.ids.space,
    environmentId: sdk.ids.environment,
    entryId,
    contentType,
    fields,
    sourceLanguage,
    targetLanguages,
    translationService,
    ...gpiCredentials,
    user: {
      email: sdk.user.email,
      firstName: sdk.user.firstName,
      lastName: sdk.user.lastName
    }
  };

  const requestUrl = `${BACKEND_URL}/api/translate/single`;

  const response = await fetch(requestUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Translation request failed');
  }

  return result;
}

/**
 * Translates multiple entries (for NewRequest dialog)
 * Returns immediately with a jobId - use pollJobUntilComplete to track progress
 */
export async function translateMultipleEntries(
  sdk: DialogAppSDK,
  entryIds: string[],
  sourceLanguage: string,
  targetLanguages: string[],
  translationService: string,
  title?: string,
  comments?: string
): Promise<JobSubmitResponse> {
  const gpiCredentials = getGPICredentials(sdk);
  const accessToken = getAccessToken(sdk);

  const response = await fetch(`${BACKEND_URL}/api/translate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      accessToken,
      spaceId: sdk.ids.space,
      environmentId: sdk.ids.environment,
      entryIds,
      sourceLanguage,
      targetLanguages,
      translationService,
      title,
      comments,
      ...gpiCredentials,
      user: {
        email: sdk.user.email,
        firstName: sdk.user.firstName,
        lastName: sdk.user.lastName
      }
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Translation request failed');
  }

  return result;
}

/**
 * Imports translated content from a completed GPI project
 * Returns immediately with a jobId - use pollJobUntilComplete to track progress
 */
export async function importTranslations(
  sdk: DialogAppSDK | PageAppSDK,
  quoteId: number,
  projectName:string
): Promise<JobSubmitResponse> {
  const gpiCredentials = getGPICredentials(sdk);
  const accessToken = getAccessToken(sdk);

  const response = await fetch(`${BACKEND_URL}/api/translate/import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      accessToken,
      spaceId: sdk.ids.space,
      environmentId: sdk.ids.environment,
      quoteId,
      projectName,
      user:{
        email: sdk.user.email,
        firstName: sdk.user.firstName,
        lastName: sdk.user.lastName
      },
      ...gpiCredentials
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Import failed');
  }

  return result;
}

/**
 * Checks if the backend server is healthy
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/translate/health`);
    const result = await response.json();
    return result.success && result.data?.status === 'healthy';
  } catch {
    return false;
  }
}

/**
 * Gets the status of a job by ID
 */
export async function getJobStatus(jobId: string): Promise<JobStatusResponse> {
  const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to get job status');
  }

  return result;
}

/**
 * Polls a job until it completes or fails
 * Calls onProgress callback with each status update
 */
export async function pollJobUntilComplete(
  jobId: string,
  onProgress?: ProgressCallback
): Promise<Job> {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const result = await getJobStatus(jobId);
        const job = result.data?.job;

        if (!job) {
          reject(new Error('Job not found'));
          return;
        }

        // Call progress callback
        if (onProgress) {
          onProgress(job);
        }

        // Check if job is complete
        if (job.status === 'completed') {
          resolve(job);
          return;
        }

        if (job.status === 'failed') {
          reject(new Error(job.error || 'Job failed'));
          return;
        }

        // Continue polling
        setTimeout(poll, POLL_INTERVAL);
      } catch (error) {
        reject(error);
      }
    };

    // Start polling
    poll();
  });
}

/**
 * Fetches jobs for a space with optional filters
 */
export async function fetchJobs(
  spaceId: string,
  options?: {
    status?: JobStatus;
    limit?: number;
    offset?: number;
  }
): Promise<JobsListResponse> {
  const params = new URLSearchParams({ spaceId });

  if (options?.status) {
    params.append('status', options.status);
  }
  if (options?.limit) {
    params.append('limit', options.limit.toString());
  }
  if (options?.offset) {
    params.append('offset', options.offset.toString());
  }

  const response = await fetch(`${BACKEND_URL}/api/jobs?${params.toString()}`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to fetch jobs');
  }

  return result;
}

/**
 * Deletes a job by ID
 */
export async function deleteJob(jobId: string): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`, {
    method: 'DELETE'
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to delete job');
  }

  return result;
}
